import { useShallow } from 'zustand/react/shallow';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Permissions as P,
  canActOnMember,
  canManageRole,
  parsePermissions,
  serializePermissions,
  type Ban,
  type Invite,
  type Role,
} from '@comms/shared';
import { useStore } from '../../store';
import { api, uploadFile } from '../../lib/api';
import { has, usePermissions } from '../../hooks/permissions';
import { Avatar, Button, ErrorText, Input, cx } from '../ui';
import { PERMISSION_GROUPS } from './permissionLabels';
import { EmojiSettings, StickerSettings } from './Expressions';
import { AutomodSettings } from './Automod';
import { useIsMobile } from '../../hooks/mobile';
import { BotTag, GuildBots } from './Bots';

type Section =
  | 'overview'
  | 'roles'
  | 'emoji'
  | 'stickers'
  | 'automod'
  | 'members'
  | 'bots'
  | 'invites'
  | 'bans'
  | 'audit';

export function GuildSettingsModal({ guildId, onClose }: { guildId: string; onClose: () => void }) {
  const guild = useStore((s) => s.guilds[guildId]);
  const meId = useStore((s) => s.me!.id);
  const perms = usePermissions(guildId);
  const [section, setSection] = useState<Section>('overview');
  const mobile = useIsMobile();
  // Phones: a list of sections first, then one section at a time with a back arrow.
  const [menu, setMenu] = useState(true);
  if (!guild) return null;
  const sections: { id: Section; label: string; show: boolean }[] = [
    { id: 'overview', label: 'Overview', show: has(perms, P.MANAGE_GUILD) },
    { id: 'roles', label: 'Roles', show: has(perms, P.MANAGE_ROLES) },
    { id: 'emoji', label: 'Emoji', show: has(perms, P.MANAGE_EMOJIS) },
    { id: 'stickers', label: 'Stickers', show: has(perms, P.MANAGE_EMOJIS) },
    { id: 'automod', label: 'AutoMod', show: has(perms, P.MANAGE_GUILD) },
    { id: 'members', label: 'Members', show: true },
    { id: 'bots', label: 'Bots', show: has(perms, P.MANAGE_GUILD) },
    { id: 'invites', label: 'Invites', show: has(perms, P.MANAGE_GUILD) },
    { id: 'bans', label: 'Bans', show: has(perms, P.BAN_MEMBERS) },
    { id: 'audit', label: 'Audit Log', show: has(perms, P.VIEW_AUDIT_LOG) },
  ];
  return (
    <div className="safe-y fixed inset-0 z-50 flex bg-bg-2">
      <nav
        className={cx(
          'flex shrink-0 flex-col bg-bg-1',
          mobile ? 'w-full px-4 py-14' : 'w-64 items-end py-14 pr-2',
          mobile && !menu && 'hidden',
        )}
      >
        <div className={mobile ? 'w-full' : 'w-48'}>
          <h3 className="mb-2 truncate px-2 text-xs font-bold uppercase text-text-3">
            {guild.name}
          </h3>
          {sections
            .filter((s) => s.show)
            .map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => (setSection(s.id), setMenu(false))}
                className={cx(
                  'block w-full rounded px-2 text-left',
                  mobile ? 'py-3' : 'py-1.5',
                  section === s.id ? 'bg-bg-active text-text-0' : 'text-text-2 hover:bg-bg-hover',
                )}
              >
                {s.label}
              </button>
            ))}
        </div>
      </nav>
      <div
        className={cx(
          'flex-1 overflow-y-auto',
          mobile ? 'px-4 py-14' : 'px-10 py-14',
          mobile && menu && 'hidden',
        )}
      >
        <div className="max-w-3xl">
          {mobile && (
            <button
              type="button"
              onClick={() => setMenu(true)}
              className="mb-4 flex items-center gap-1 text-sm text-text-2 hover:text-text-0"
            >
              ‹ {guild.name} settings
            </button>
          )}
          {section === 'overview' && (
            <Overview guildId={guildId} isOwner={guild.ownerId === meId} onClose={onClose} />
          )}
          {section === 'roles' && <Roles guildId={guildId} />}
          {section === 'emoji' && <EmojiSettings guildId={guildId} />}
          {section === 'stickers' && <StickerSettings guildId={guildId} />}
          {section === 'automod' && <AutomodSettings guildId={guildId} />}
          {section === 'members' && <Members guildId={guildId} />}
          {section === 'bots' && <GuildBots guildId={guildId} />}
          {section === 'invites' && <Invites guildId={guildId} />}
          {section === 'bans' && <Bans guildId={guildId} />}
          {section === 'audit' && <AuditLog guildId={guildId} />}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close settings"
        className={cx(
          'absolute rounded-full border-2 border-text-3 text-text-3 hover:text-text-0',
          // Phones: below the status bar / Dynamic Island, with a full-size tap target.
          mobile
            ? 'right-3 top-[calc(env(safe-area-inset-top)+0.5rem)] flex h-10 items-center gap-1 bg-bg-2 px-3 text-sm'
            : 'right-8 top-8 px-2.5 py-1',
        )}
      >
        ✕ <span className={mobile ? '' : 'text-xs'}>{mobile ? 'Close' : 'ESC'}</span>
      </button>
      <EscClose onClose={onClose} />
    </div>
  );
}

function EscClose({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [onClose]);
  return null;
}

function Overview({
  guildId,
  isOwner,
  onClose,
}: {
  guildId: string;
  isOwner: boolean;
  onClose: () => void;
}) {
  const guild = useStore((s) => s.guilds[guildId]!);
  const textChannels = useStore(
    useShallow((s) =>
      Object.values(s.channels).filter((c) => c.guildId === guildId && c.type === 0),
    ),
  );
  const members = useStore(useShallow((s) => Object.values(s.members[guildId] ?? {})));
  const [name, setName] = useState(guild.name);
  const [description, setDescription] = useState(guild.description ?? '');
  const [systemChannelId, setSystemChannelId] = useState(guild.systemChannelId ?? '');
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();

  async function save(extra: Record<string, unknown> = {}) {
    try {
      setError(null);
      await api('PATCH', `/guilds/${guildId}`, {
        name,
        description: description || null,
        systemChannelId: systemChannelId || null,
        ...extra,
      });
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  }
  async function uploadIcon(file: File) {
    const att = await uploadFile(file, file.name);
    await save({ iconAttachmentId: att.id });
  }
  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-text-0">Server Overview</h2>
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-accent text-2xl text-white">
          {guild.iconUrl ? (
            <img src={guild.iconUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            guild.name[0]
          )}
        </div>
        <label className="cursor-pointer rounded bg-bg-active px-3 py-2 text-sm text-text-0 hover:bg-bg-3">
          Upload icon
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={(e) => e.target.files?.[0] && void uploadIcon(e.target.files[0])}
          />
        </label>
      </div>
      <Input label="Server name" value={name} onChange={(e) => setName(e.target.value)} />
      <Input
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <label className="block text-sm text-text-2">
        System messages channel
        <select
          value={systemChannelId}
          onChange={(e) => setSystemChannelId(e.target.value)}
          className="mt-1 w-full rounded bg-bg-0 p-2 text-text-0"
        >
          <option value="">None</option>
          {textChannels.map((c) => (
            <option key={c.id} value={c.id}>
              #{c.name}
            </option>
          ))}
        </select>
      </label>
      <ErrorText error={error} />
      <Button onClick={() => void save()}>{saved ? 'Saved' : 'Save changes'}</Button>
      {isOwner && (
        <div className="space-y-3 border-t border-bg-3 pt-5">
          <h3 className="font-semibold text-text-0">Danger zone</h3>
          <div className="flex items-center gap-2">
            <select
              id="transfer"
              className="rounded bg-bg-0 p-2 text-sm text-text-0"
              defaultValue=""
            >
              <option value="">Transfer ownership to…</option>
              {members
                .filter((m) => m.user.id !== guild.ownerId)
                .map((m) => (
                  <option key={m.user.id} value={m.user.id}>
                    {m.user.displayName}
                  </option>
                ))}
            </select>
            <Button
              variant="secondary"
              onClick={async () => {
                const id = (document.getElementById('transfer') as HTMLSelectElement).value;
                if (id && confirm('Transfer ownership? You will lose owner powers.'))
                  await api('POST', `/guilds/${guildId}/transfer`, { ownerId: id });
              }}
            >
              Transfer
            </Button>
          </div>
          <Button
            variant="danger"
            onClick={async () => {
              if (
                prompt(`Type the server name (${guild.name}) to delete it permanently`) !==
                guild.name
              )
                return;
              await api('DELETE', `/guilds/${guildId}`);
              onClose();
              navigate('/channels/@me');
            }}
          >
            Delete server
          </Button>
        </div>
      )}
    </div>
  );
}

function Roles({ guildId }: { guildId: string }) {
  const guild = useStore((s) => s.guilds[guildId]!);
  const me = useStore((s) => s.members[guildId]?.[s.me!.id]);
  const meId = useStore((s) => s.me!.id);
  const myPerms = usePermissions(guildId);
  const roles = [...guild.roles].sort((a, b) => b.position - a.position);
  const [selectedId, setSelectedId] = useState(roles[0]?.id ?? guildId);
  const selected = roles.find((r) => r.id === selectedId);
  const [draft, setDraft] = useState<Role | null>(selected ?? null);
  const [error, setError] = useState<unknown>(null);
  useEffect(
    () => setDraft(guild.roles.find((r) => r.id === selectedId) ?? null),
    [selectedId, guild.roles],
  );

  const guildCtx = {
    ownerId: guild.ownerId,
    roles: guild.roles.map((r) => ({
      id: r.id,
      permissions: parsePermissions(r.permissions),
      position: r.position,
    })),
  };
  const editable = (r: Role) =>
    r.id === guildId ||
    canManageRole(guildCtx, { userId: meId, roleIds: me?.roles ?? [] }, r.position);

  async function create() {
    try {
      const r = await api<Role>('POST', `/guilds/${guildId}/roles`, { name: 'new role' });
      setSelectedId(r.id);
    } catch (err) {
      setError(err);
    }
  }
  async function save() {
    if (!draft) return;
    try {
      setError(null);
      await api('PATCH', `/guilds/${guildId}/roles/${draft.id}`, {
        ...(draft.id !== guildId ? { name: draft.name } : {}),
        color: draft.color,
        hoist: draft.hoist,
        mentionable: draft.mentionable,
        permissions: draft.permissions,
      });
    } catch (err) {
      setError(err);
    }
  }
  async function move(r: Role, delta: number) {
    const ordered = roles.filter((x) => x.id !== guildId).reverse(); // ascending
    const i = ordered.findIndex((x) => x.id === r.id);
    const j = i + delta;
    if (j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j]!, ordered[i]!];
    await api(
      'PATCH',
      `/guilds/${guildId}/roles`,
      ordered.map((x, idx) => ({ id: x.id, position: idx + 1 })),
    ).catch(setError);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-text-0">Roles</h2>
        <Button onClick={create}>Create role</Button>
      </div>
      <p className="mb-4 text-sm text-text-3">
        Members use the color of their highest role. Roles higher in the list can manage those below
        them.
      </p>
      <div className="flex gap-6">
        <div className="w-52 shrink-0 space-y-1">
          {roles.map((r) => (
            <div
              key={r.id}
              className={cx(
                'group flex items-center rounded px-2 py-1.5',
                r.id === selectedId ? 'bg-bg-active' : 'hover:bg-bg-hover',
              )}
            >
              <button
                type="button"
                onClick={() => setSelectedId(r.id)}
                className="flex flex-1 items-center gap-2 truncate text-left text-sm text-text-1"
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{
                    background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : '#99aab5',
                  }}
                />
                {r.name}
              </button>
              {r.id !== guildId && editable(r) && (
                <span className="hidden gap-1 text-text-3 group-hover:flex">
                  <button type="button" onClick={() => void move(r, 1)} title="Move up">
                    ▲
                  </button>
                  <button type="button" onClick={() => void move(r, -1)} title="Move down">
                    ▼
                  </button>
                </span>
              )}
            </div>
          ))}
        </div>
        {draft && (
          <div className="flex-1 space-y-4">
            {!editable(draft) && (
              <p className="rounded bg-warning/20 p-2 text-sm text-text-0">
                This role is at or above your highest role, so you can't edit it.
              </p>
            )}
            <fieldset disabled={!editable(draft)} className="space-y-4">
              {draft.id !== guildId && (
                <Input
                  label="Role name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              )}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-text-1">
                  Color
                  <input
                    type="color"
                    value={`#${draft.color.toString(16).padStart(6, '0')}`}
                    onChange={(e) =>
                      setDraft({ ...draft, color: parseInt(e.target.value.slice(1), 16) })
                    }
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-text-1">
                  <input
                    type="checkbox"
                    checked={draft.hoist}
                    onChange={(e) => setDraft({ ...draft, hoist: e.target.checked })}
                  />{' '}
                  Show separately
                </label>
                <label className="flex items-center gap-2 text-sm text-text-1">
                  <input
                    type="checkbox"
                    checked={draft.mentionable}
                    onChange={(e) => setDraft({ ...draft, mentionable: e.target.checked })}
                  />{' '}
                  Mentionable
                </label>
              </div>
              {PERMISSION_GROUPS.map((g) => (
                <div key={g.title}>
                  <h4 className="mb-1 text-xs font-bold uppercase text-text-3">{g.title}</h4>
                  {g.items.map((i) => {
                    const flag = P[i.key];
                    const on = has(parsePermissions(draft.permissions), flag);
                    const grantable = has(myPerms, flag);
                    return (
                      <label
                        key={i.key}
                        className={cx(
                          'flex items-center justify-between border-b border-bg-3 py-2',
                          !grantable && 'opacity-50',
                        )}
                      >
                        <span>
                          <span className="block text-sm text-text-0">{i.label}</span>
                          <span className="text-xs text-text-3">{i.desc}</span>
                        </span>
                        <input
                          type="checkbox"
                          disabled={!grantable}
                          checked={on}
                          onChange={(e) => {
                            const cur = parsePermissions(draft.permissions);
                            setDraft({
                              ...draft,
                              permissions: serializePermissions(
                                e.target.checked ? cur | flag : cur & ~flag,
                              ),
                            });
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              ))}
              <div className="flex gap-2">
                <Button onClick={save}>Save role</Button>
                {draft.id !== guildId && (
                  <Button
                    variant="danger"
                    onClick={async () => {
                      if (!confirm(`Delete role ${draft.name}?`)) return;
                      await api('DELETE', `/guilds/${guildId}/roles/${draft.id}`).catch(setError);
                      setSelectedId(guildId);
                    }}
                  >
                    Delete role
                  </Button>
                )}
              </div>
            </fieldset>
            <ErrorText error={error} />
          </div>
        )}
      </div>
    </div>
  );
}

function Members({ guildId }: { guildId: string }) {
  const guild = useStore((s) => s.guilds[guildId]!);
  const members = useStore(useShallow((s) => Object.values(s.members[guildId] ?? {})));
  const me = useStore((s) => s.members[guildId]?.[s.me!.id]);
  const meId = useStore((s) => s.me!.id);
  const perms = usePermissions(guildId);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<unknown>(null);
  const guildCtx = {
    ownerId: guild.ownerId,
    roles: guild.roles.map((r) => ({
      id: r.id,
      permissions: parsePermissions(r.permissions),
      position: r.position,
    })),
  };
  const assignable = guild.roles.filter(
    (r) =>
      r.id !== guildId &&
      canManageRole(guildCtx, { userId: meId, roleIds: me?.roles ?? [] }, r.position),
  );

  return (
    <div>
      <h2 className="mb-4 text-xl font-bold text-text-0">Members — {members.length}</h2>
      <Input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search members"
      />
      <ErrorText error={error} />
      <div className="mt-4 divide-y divide-bg-3">
        {members
          .filter((m) =>
            `${m.user.username} ${m.user.displayName} ${m.nickname ?? ''}`
              .toLowerCase()
              .includes(filter.toLowerCase()),
          )
          .map((m) => {
            const outranks = canActOnMember(
              guildCtx,
              { userId: meId, roleIds: me?.roles ?? [] },
              { userId: m.user.id, roleIds: m.roles },
            );
            return (
              <div key={m.user.id} className="flex flex-wrap items-center gap-3 py-3">
                <Avatar userId={m.user.id} size={32} />
                <div className="min-w-40 flex-1">
                  <div className="text-text-0">
                    {m.nickname ?? m.user.displayName}
                    {m.user.bot && <BotTag />}
                  </div>
                  <div className="text-xs text-text-3">{m.user.username}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {guild.roles
                    .filter((r) => m.roles.includes(r.id))
                    .map((r) => (
                      <span
                        key={r.id}
                        className="rounded bg-bg-1 px-1.5 py-0.5 text-xs text-text-1"
                      >
                        {r.name}
                      </span>
                    ))}
                </div>
                {has(perms, P.MANAGE_ROLES) &&
                  assignable.length > 0 &&
                  (outranks || m.user.id === meId) && (
                    <select
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        const roles = m.roles.includes(id)
                          ? m.roles.filter((r) => r !== id)
                          : [...m.roles, id];
                        api('PATCH', `/guilds/${guildId}/members/${m.user.id}`, { roles }).catch(
                          setError,
                        );
                      }}
                      className="rounded bg-bg-0 p-1 text-xs text-text-1"
                    >
                      <option value="">Toggle role…</option>
                      {assignable.map((r) => (
                        <option key={r.id} value={r.id}>
                          {m.roles.includes(r.id) ? '✓ ' : ''}
                          {r.name}
                        </option>
                      ))}
                    </select>
                  )}
                {has(perms, P.KICK_MEMBERS) && outranks && (
                  <Button
                    variant="ghost"
                    className="py-1 text-xs"
                    onClick={() =>
                      confirm(`Kick ${m.user.displayName}?`) &&
                      api('DELETE', `/guilds/${guildId}/members/${m.user.id}`).catch(setError)
                    }
                  >
                    Kick
                  </Button>
                )}
                {has(perms, P.BAN_MEMBERS) && outranks && (
                  <Button
                    variant="danger"
                    className="py-1 text-xs"
                    onClick={() => {
                      const reason = prompt(`Ban ${m.user.displayName}? Reason (optional):`);
                      if (reason !== null)
                        api('PUT', `/guilds/${guildId}/bans/${m.user.id}`, {
                          reason: reason || undefined,
                          deleteMessageSeconds: 3600,
                        }).catch(setError);
                    }}
                  >
                    Ban
                  </Button>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

function Invites({ guildId }: { guildId: string }) {
  const [invites, setInvites] = useState<Invite[]>([]);
  const load = () => void api<Invite[]>('GET', `/guilds/${guildId}/invites`).then(setInvites);
  useEffect(load, [guildId]);
  return (
    <div>
      <h2 className="mb-4 text-xl font-bold text-text-0">Invites</h2>
      {invites.length === 0 && <p className="text-text-3">No active invites.</p>}
      <div className="divide-y divide-bg-3">
        {invites.map((i) => (
          <div key={i.code} className="flex items-center gap-4 py-2 text-sm">
            <code className="flex-1 text-text-0">{i.code}</code>
            <span className="text-text-3">
              {i.uses}/{i.maxUses || '∞'} uses
            </span>
            <span className="text-text-3">
              {i.expiresAt ? new Date(i.expiresAt).toLocaleString() : 'never expires'}
            </span>
            <Button
              variant="ghost"
              className="py-1 text-danger"
              onClick={() => void api('DELETE', `/invites/${i.code}`).then(load)}
            >
              Revoke
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bans({ guildId }: { guildId: string }) {
  const [bans, setBans] = useState<Ban[]>([]);
  const load = () => void api<Ban[]>('GET', `/guilds/${guildId}/bans`).then(setBans);
  useEffect(load, [guildId]);
  return (
    <div>
      <h2 className="mb-4 text-xl font-bold text-text-0">Bans</h2>
      {bans.length === 0 && <p className="text-text-3">Nobody is banned.</p>}
      {bans.map((b) => (
        <div key={b.user.id} className="flex items-center gap-3 border-b border-bg-3 py-2">
          <span className="flex-1 text-text-0">{b.user.displayName}</span>
          <span className="text-sm text-text-3">{b.reason ?? 'No reason'}</span>
          <Button
            variant="secondary"
            className="py-1"
            onClick={() => void api('DELETE', `/guilds/${guildId}/bans/${b.user.id}`).then(load)}
          >
            Unban
          </Button>
        </div>
      ))}
    </div>
  );
}

function AuditLog({ guildId }: { guildId: string }) {
  const users = useStore((s) => s.users);
  const [entries, setEntries] = useState<
    {
      id: string;
      actorId: string | null;
      action: string;
      targetId: string | null;
      createdAt: string;
      data: Record<string, unknown>;
    }[]
  >([]);
  useEffect(
    () => void api<typeof entries>('GET', `/guilds/${guildId}/audit-log`).then(setEntries),
    [guildId],
  );
  return (
    <div>
      <h2 className="mb-4 text-xl font-bold text-text-0">Audit Log</h2>
      <div className="divide-y divide-bg-3 text-sm">
        {entries.map((e) => (
          <div key={e.id} className="py-2">
            <span className="font-medium text-text-0">
              {(e.actorId && users[e.actorId]?.displayName) ?? 'Someone'}
            </span>{' '}
            <span className="text-text-2">{e.action.toLowerCase().replace(/_/g, ' ')}</span>
            {e.targetId && users[e.targetId] && (
              <span className="text-text-0"> {users[e.targetId]!.displayName}</span>
            )}
            <span className="ml-2 text-xs text-text-3">
              {new Date(e.createdAt).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
